/**
 * Geolocation Service
 *
 * Provides high-accuracy GPS location retrieval using Capacitor Geolocation plugin.
 * Handles permissions, error cases, and accuracy validation.
 */

import { Geolocation } from '@capacitor/geolocation'
import type { Position } from '@capacitor/geolocation'
import type { PositionResult, GeolocationError } from '@/types/geofence'
import { ACCEPTABLE_GPS_ACCURACY } from '@/utils/constants'
import { hasLocationPermission, requestLocationPermissions } from './permissions'

/**
 * Get current GPS position with high-accuracy mode
 * @returns Position result or error
 */
export async function getCurrentPosition(): Promise<PositionResult | GeolocationError> {
    try {
        // Check permissions first
        const hasPermission = await hasLocationPermission()
        if (!hasPermission) {
            // Request permissions
            const permissionStatus = await requestLocationPermissions()
            if (permissionStatus.location !== 'granted') {
                const error = {
                    error: 'PERMISSION_DENIED',
                    message: 'Location permission is required. Please enable in settings.',
                    userMessage: 'Location permission is required. Please enable in settings.',
                    action: 'Go to Settings > Privacy & Security > Location Services',
                    severity: 'error' as const
                }

                // Debug logging
                console.log('[GeolocationService] Permission denied:', {
                    timestamp: new Date().toISOString(),
                    permissionStatus,
                    error: error.error
                })

                return error
            }
        }

        // Try high-accuracy first, then fallback to standard accuracy
        let position: Position
        try {
            // Get current position with high-accuracy mode
            position = await Geolocation.getCurrentPosition({
                enableHighAccuracy: true,
                timeout: 15000, // 15 seconds for high accuracy (reduced for faster feedback)
                maximumAge: 30000 // Allow cached position up to 30 seconds old
            })
        } catch (highAccuracyError) {
            console.log('[GeolocationService] High accuracy failed, trying standard accuracy:', {
                timestamp: new Date().toISOString(),
                error: highAccuracyError
            })

            // Fallback to standard accuracy if high accuracy fails
            position = await Geolocation.getCurrentPosition({
                enableHighAccuracy: false,
                timeout: 10000, // 10 seconds for standard accuracy
                maximumAge: 60000 // Allow older cached positions
            })
        }

        const result: PositionResult = {
            coords: {
                lat: position.coords.latitude,
                lng: position.coords.longitude
            },
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp
        }

        // Debug logging for location checks
        console.log('[GeolocationService] Location check:', {
            timestamp: new Date().toISOString(),
            coordinates: { lat: result.coords.lat, lng: result.coords.lng },
            accuracy: result.accuracy,
            result: 'success'
        })

        return result
    } catch (error: unknown) {
        return handleGeolocationError(error)
    }
}

/**
 * Check if GPS accuracy is acceptable (≤50 meters)
 * @param accuracy - GPS accuracy in meters
 * @returns true if accuracy is acceptable, false otherwise
 */
export function isAccuracyAcceptable(accuracy: number): boolean {
    return accuracy <= ACCEPTABLE_GPS_ACCURACY
}

/**
 * Create accuracy insufficient error
 * @param accuracy - Current GPS accuracy in meters
 * @returns GeolocationError for accuracy insufficient
 */
export function createAccuracyInsufficientError(accuracy: number): GeolocationError {
    const geolocationError = {
        error: 'ACCURACY_INSUFFICIENT',
        message: `GPS accuracy is ${accuracy.toFixed(0)}m, but must be ≤${ACCEPTABLE_GPS_ACCURACY}m for geofence validation`,
        userMessage: 'GPS accuracy low. Move to open area for better signal.',
        action: 'Move to an open area with clear sky view',
        severity: 'warning' as const
    }

    console.log('[GeolocationService] Accuracy insufficient error:', {
        timestamp: new Date().toISOString(),
        accuracy,
        threshold: ACCEPTABLE_GPS_ACCURACY,
        error: geolocationError.error,
        userMessage: geolocationError.userMessage
    })

    return geolocationError
}

/**
 * Handle geolocation errors and return user-friendly error messages
 * @param error - Error from Geolocation API
 * @returns GeolocationError with error code and message
 */
function handleGeolocationError(error: unknown): GeolocationError {
    console.error('[GeolocationService] Error occurred:', error)

    if (error && typeof error === 'object' && 'message' in error) {
        const errorMessage = (error as { message: string }).message.toLowerCase()

        // Permission denied
        if (
            errorMessage.includes('permission') ||
            errorMessage.includes('denied') ||
            errorMessage.includes('not authorized')
        ) {
            const geolocationError = {
                error: 'PERMISSION_DENIED',
                message: 'Location permission is required. Please enable in settings.',
                userMessage: 'Location permission is required. Please enable in settings.',
                action: 'Go to Settings > Privacy & Security > Location Services',
                severity: 'error' as const
            }

            console.log('[GeolocationService] Permission denied error:', {
                timestamp: new Date().toISOString(),
                error: geolocationError.error,
                userMessage: geolocationError.userMessage
            })

            return geolocationError
        }

        // Timeout
        if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
            const geolocationError = {
                error: 'TIMEOUT',
                message: 'Location request timed out. GPS signal may be weak.',
                userMessage: 'GPS signal weak. Try moving to an open area or near a window.',
                action: 'Move to an open area with clear sky view and try again',
                severity: 'warning' as const
            }

            console.log('[GeolocationService] Timeout error:', {
                timestamp: new Date().toISOString(),
                error: geolocationError.error,
                userMessage: geolocationError.userMessage
            })

            return geolocationError
        }

        // Location unavailable
        if (
            errorMessage.includes('unavailable') ||
            errorMessage.includes('not available') ||
            errorMessage.includes('gps')
        ) {
            const geolocationError = {
                error: 'LOCATION_UNAVAILABLE',
                message: 'GPS signal required. Please move to an open area.',
                userMessage: 'GPS signal required. Please move to an open area.',
                action: 'Move to an open area with clear sky view',
                severity: 'error' as const
            }

            console.log('[GeolocationService] GPS unavailable error:', {
                timestamp: new Date().toISOString(),
                error: geolocationError.error,
                userMessage: geolocationError.userMessage
            })

            return geolocationError
        }
    }

    // Generic error
    const geolocationError = {
        error: 'LOCATION_UNAVAILABLE',
        message: 'GPS signal required. Please move to an open area.',
        userMessage: 'GPS signal required. Please move to an open area.',
        action: 'Move to an open area with clear sky view',
        severity: 'error' as const
    }

    console.log('[GeolocationService] Generic error:', {
        timestamp: new Date().toISOString(),
        error: geolocationError.error,
        userMessage: geolocationError.userMessage,
        originalError: error
    })

    return geolocationError
}
